import type { ShopSession } from '@/services/shopSession/ShopSession.types'
import { Features } from '@/utils/Features'
import { BUNDLE_DISCOUNT_CAMPAIGN_CODE, BUNDLE_DISCOUNT_ELIGIBLE_PRODUCT_IDS } from './bundleDiscount.constants'

export const isBundleDiscountEnabled = (shopSession: ShopSession) => {
  return Features.enabled('BUNDLE_DISCOUNT') || shopSession.experiments?.bundleDiscount
}

export const hasBundleDiscount = (shopSession: ShopSession) => {
  if (!isBundleDiscountEnabled(shopSession)) {
    return false
  }

  const hasBundleDiscountOnAtLeastOneItem = shopSession.cart.items.some((item) =>
    item.productOffer.cost.discounts.some((discount) => discount.campaignCode === BUNDLE_DISCOUNT_CAMPAIGN_CODE),
  )

  return shopSession.cart.redeemedCampaignV2 ?? hasBundleDiscountOnAtLeastOneItem
}

/**
 * Determines if the shop session has a "fake" bundle discount.
 *
 * The `shopSession.experiments.bundleDiscount` flag is set to true when the session is eligible
 * for bundle discounts based on logic in storefront. This eligibility requires
 * that no other campaign code is redeemed - if any other campaign is active, the session becomes
 * ineligible. However, this flag can be true even when no campaign is redeemed - it only indicates
 * eligibility, not actual redemption.
 *
 * A "fake" bundle discount occurs when:
 * 1. The session is eligible for bundle discounts (bundleDiscount flag is true)
 * 2. AND a campaign is actually redeemed (redeemedCampaignV2 is present)
 *
 * This is different from a "real" bundle discount which uses the actual BUNDLE_DISCOUNT feature
 * flag and applies discounts directly to cart items.
 */
export const hasFakeBundleDiscount = (shopSession: ShopSession): boolean => {
  if (!shopSession.experiments?.bundleDiscount) {
    return false
  }

  return !!shopSession.cart.redeemedCampaignV2
}

export const hasCartItemsEligibleForBundleDiscount = (shopSession: ShopSession) => {
  return (
    isBundleDiscountEnabled(shopSession) &&
    // Another special case to ignore - only accident in cart
    shopSession.cart.items.some((item) => BUNDLE_DISCOUNT_ELIGIBLE_PRODUCT_IDS.has(item.productOffer.product.id))
  )
}

export const hasCartItemsEligibleForRealBundleDiscount = (shopSession: ShopSession) => {
  return Features.enabled('BUNDLE_DISCOUNT') && (shopSession.cart.bundleDiscount.eligibleProductCount ?? 0) >= 1
}
